import { useQuery } from '@tanstack/react-query';
import { approvalMatrixHealthApi } from '../../api/admin/roles';
import { SectionHeader } from '../../components/SectionHeader';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';

/**
 * Extracted so AccessManagementPage can embed this as a tab -
 * ApprovalMatrixHealthPage below is now a thin wrapper for direct/
 * bookmarked links to the old route.
 *
 * This would have caught the exact real issue hit earlier (a role
 * configured to approve project budgets with no user_approval_limits
 * row set for anyone holding it) - it's the honest "will this actually
 * work" check, not a cosmetic status page, so it belongs where people
 * are actually setting up access, not hidden on a separate page.
 */
export function ApprovalMatrixHealthContent() {
  const healthQuery = useQuery({ queryKey: ['approval-matrix-health'], queryFn: () => approvalMatrixHealthApi.check() });
  const data = healthQuery.data?.data;

  return (
    <>
      <p className="muted">Checks every approval rule to confirm it will actually work: does someone hold the role, do they have the right access, and has a money limit been set where needed. This is what would have caught "no user currently holding this role qualifies" before it happened.</p>

      {data && data.issues.length > 0 && (
        <div className="panel">
          <SectionHeader title={`Problems Found (${data.issues.length})`} />
          <div className="panel-body">
          <DataTable
            rows={data.issues}
            rowKey={(i) => `${i.module}-${i.approverRole}`}
            emptyMessage=""
            columns={[
              { header: 'What this covers', render: (i) => i.module },
              { header: 'Who should approve', render: (i) => i.approverRole },
              { header: 'Condition', render: (i) => `${i.conditionType} ${i.conditionValue}` },
              { header: 'People with this role', render: (i) => i.usersHoldingRole },
              { header: 'What\'s wrong', render: (i) => <ul style={{ margin: 0, paddingLeft: '1.1em' }}>{i.missingPermissions.map((m) => <li key={m}>{m}</li>)}</ul> },
            ]}
          />
          </div>
        </div>
      )}

      {data && data.issues.length === 0 && (
        <section className="card"><p>No problems found - every approval rule can actually be used right now.</p></section>
      )}

      {data && data.healthy.length > 0 && (
        <div className="panel">
          <SectionHeader title={`Working Correctly (${data.healthy.length})`} />
          <div className="panel-body">
          <DataTable
            rows={data.healthy}
            rowKey={(i) => `${i.module}-${i.approverRole}`}
            emptyMessage=""
            columns={[
              { header: 'What this covers', render: (i) => i.module },
              { header: 'Who approves', render: (i) => i.approverRole },
              { header: 'Condition', render: (i) => `${i.conditionType} ${i.conditionValue}` },
            ]}
          />
          </div>
        </div>
      )}
    </>
  );
}

export function ApprovalMatrixHealthPage() {
  return (
    <div className="page">
      <PageHeader title="Approval Matrix Health" description="Checks every configured approval rule against what's actually set up, so a broken combination is caught here instead of when a real request gets stuck." />
      <ApprovalMatrixHealthContent />
    </div>
  );
}
